import React, { useEffect, useState } from "react";
import { Circle, Layer, Line, Text } from "react-konva";
import checkForOverlap from "./overlapCheck.component";

export default function CustomMask(props) {
    const shapeRef = React.useRef();
    const layerRef = React.useRef();

    const [points, setPoints] = React.useState(null);
    const [isDragging, setIsDragging] = React.useState(false);
    const [initialPos, setInitialPos] = React.useState({ x: 0, y: 0 });
    const [overlapInfo, setOverlapInfo] = useState([]);

    const { mask, maskList, stageScale } = props;

    useEffect(() => {
        if (mask.points && Array.isArray(mask.points)) {
            const flatPoints = [];
            mask.points.slice(0, -1).forEach(point => {
                if (point && typeof point.x === "number" && typeof point.y === "number") {
                    flatPoints.push(point.x, point.y);
                } else {
                    console.log("Invalid point:", point);
                }
            });
            setPoints(flatPoints);
        } else {
            console.log("Invalid mask points:", mask.points);
        }
    }, [mask.points]);


    var selected = mask.selected;
    const color = mask.color || "lightblue";
    const strokeWidth = Math.max(0.33, 3 / stageScale);
    const circleRadius = Math.max(0.5, 5 / stageScale);

    const handleLineDragStart = (e) => {
        setIsDragging(true);
        setInitialPos({ x: e.target.x(), y: e.target.y() });
    };

    const handleLineDragEnd = (e) => {
        setIsDragging(false);
        const dx = e.target.x() - initialPos.x;
        const dy = e.target.y() - initialPos.y;

        const updatedPoints = points.map((coord, index) =>
            index % 2 === 0 ? coord + dx : coord + dy
        );

        setPoints(updatedPoints);

        const xyPoints = [];
        for (let i = 0; i < updatedPoints.length; i += 2) {
            xyPoints.push({ x: updatedPoints[i], y: updatedPoints[i + 1] });
        }

        if (xyPoints.length > 0) {
            xyPoints.push({ ...xyPoints[0] });
        }

        props.updateMask(null, props.i, "polygon", xyPoints);
        e.target.position({ x: 0, y: 0 });
    };

    const handleCircleDragMove = (e, index) => {
        if (!shapeRef.current) return;
        const lineX = shapeRef.current.x();
        const lineY = shapeRef.current.y();

        const newPoints = [...points];
        newPoints[index * 2] = e.target.x() - lineX;
        newPoints[index * 2 + 1] = e.target.y() - lineY;
        setPoints(newPoints);

        const xyPoints = [];
        for (let i = 0; i < newPoints.length; i += 2) {
            xyPoints.push({ x: newPoints[i], y: newPoints[i + 1] });
        }

        if (xyPoints.length > 0) {
            xyPoints.push({ ...xyPoints[0] });
        }
    };

    const handleCircleDragEnd = (e, index) => {
        const lineX = shapeRef.current.x();
        const lineY = shapeRef.current.y();
        const newPoints = [...points];
        newPoints[index * 2] = e.target.x() - lineX;
        newPoints[index * 2 + 1] = e.target.y() - lineY;
        setPoints(newPoints);

        const xyPoints = [];
        for (let i = 0; i < newPoints.length; i += 2) {
            xyPoints.push({ x: newPoints[i], y: newPoints[i + 1] });
        }

        if (xyPoints.length > 0) {
            xyPoints.push({ ...xyPoints[0] });
        }

        props.updateMask(null, props.i, "polygon", xyPoints);
    };


    const handleContextMenu = (e, maskIndex, pointIndex) => {
        console.log("handleContextMenu", maskIndex, pointIndex)
        props.handleContextMenu(e, maskIndex, pointIndex)
    }

    const getDynamicFontSize = () => {
        return Math.max(4, 18 / props.stageScale);
    };

    const handleSelect = React.useCallback(() => {
        props.selectMask(mask.id);
    }, [props.selectMask, mask.id, maskList]);

    const getTextPosition = () => {
        const dynamicFontSize = getDynamicFontSize();
        const fontOffset = dynamicFontSize + 0.5;

        if (!Array.isArray(points) || points.length === 0) {
            return { x: 0, y: 0 };
        }

        const minYPointIndex = points.reduce((minIndex, _, index) => {
            if (index % 2 !== 0) {
                return points[index] < points[minIndex] ? index : minIndex;
            }
            return minIndex;
        }, 1);

        const minXPoint = {
            x: points[minYPointIndex - 1],
            y: points[minYPointIndex],
        };

        if (!minXPoint || minXPoint.x === undefined || minXPoint.y === undefined) {
            return { x: 0, y: 0 };
        }

        return {
            x: minXPoint.x,
            y: minXPoint.y - fontOffset,
        };
    };

    const textPosition = getTextPosition();


    useEffect(() => {
        const overlapInfo = checkForOverlap(props.maskList);
        setOverlapInfo(overlapInfo || []);
    }, [props.maskList]);

    const sortPointsClockwise = (overlap) => {
        const points = overlap.overlapPoints;
        if (points.length < 3) return points;
        if (overlap.mask1Type === "polygon" || overlap.mask2Type === "polygon") {
            // console.log("calling sortPolygonPoints", points);
            const polyPoints = sortPolygonPoints(points);
            if (polyPoints.length > 0) return polyPoints;
        }

        const centroid = points.reduce(
            (acc, p) => ({ x: acc.x + p.x / points.length, y: acc.y + p.y / points.length }),
            { x: 0, y: 0 }
        );

        const uniquePoints = Array.from(
            new Map(points.map(p => [`${p.x.toFixed(6)},${p.y.toFixed(6)}`, p])).values()
        );

        return uniquePoints.sort((a, b) => {
            const angleA = Math.atan2(a.y - centroid.y, a.x - centroid.x);
            const angleB = Math.atan2(b.y - centroid.y, b.x - centroid.x);
            return angleA - angleB;
        });
    };

    const sortPolygonPoints = (points) => {
        if (points.length < 3) return points;

        const round = (num) => Number(num.toFixed(4));
        const format = (point) => `${round(point.x)},${round(point.y)}`;

        const overlapSet = new Set(points.map(format));

        let polygonMask = null;
        let maxMatches = 0;
        const nonPolygonMasks = [];

        for (const mask of props.maskList) {
            if (!Array.isArray(mask.points)) continue;

            if (mask.type === "polygon") {
                const matchCount = mask.points.reduce((count, pt) =>
                    overlapSet.has(format(pt)) ? count + 1 : count
                    , 0);

                if (matchCount > maxMatches) {
                    polygonMask = mask;
                    maxMatches = matchCount;
                }
            } else {
                nonPolygonMasks.push(mask);
            }
        }

        if (!polygonMask) {
            return [];
        }

        return fitPointsIntoPolygon(polygonMask.points, points, nonPolygonMasks);
    };

    function fitPointsIntoPolygon(polygonPoints, unorderedPoints, nonPolygonMasks) {
        const round = (num) => Number(num.toFixed(4));
        const format = (pt) => `${round(pt.x)},${round(pt.y)}`;
        const unorderedSet = new Set(unorderedPoints.map(format));

        const isPointOnSegment = (p, a, b, tolerance = 1.5) => {
            const cross = Math.abs((b.y - a.y) * (p.x - a.x) - (b.x - a.x) * (p.y - a.y));
            if (cross > tolerance) return false;

            const dot = (p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y);
            if (dot < 0) return false;

            const lenSq = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
            if (dot > lenSq) return false;

            return true;
        };

        let corners = [];
        nonPolygonMasks.forEach(mask => {
            mask.points.forEach(point => {
                if (unorderedPoints.some(p => p.x === point.x && p.y === point.y)) {
                    corners.push(point);
                }
            });
        });

        const pointsToFit = unorderedPoints.slice();

        let result = [];

        for (let i = 0; i < polygonPoints.length - 1; i++) {
            const a = polygonPoints[i];
            const b = polygonPoints[i + 1];

            result.push(a);

            const pointsOnSegment = [];

            for (let j = pointsToFit.length - 1; j >= 0; j--) {
                const p = pointsToFit[j];
                if (isPointOnSegment(p, a, b)) {
                    pointsOnSegment.push(p);
                    pointsToFit.splice(j, 1);
                }
            }

            pointsOnSegment.sort((p1, p2) => {
                const d1 = Math.hypot(p1.x - a.x, p1.y - a.y);
                const d2 = Math.hypot(p2.x - a.x, p2.y - a.y);
                return d1 - d2;
            });

            result.push(...pointsOnSegment);
        }

        corners.forEach(corner => {
            const key = format(corner);
            if (result.some(pt => format(pt) === key)) return;

            let bestIndex = -1;
            let bestDist = Infinity;

            for (let i = 0; i < result.length - 1; i++) {
                const a = result[i];
                const b = result[i + 1];
                const dist = pointToSegmentDistance(corner, a, b);
                if (dist < bestDist) {
                    bestDist = dist;
                    bestIndex = i + 1;
                }
            }

            if (bestIndex >= 0) {
                result.splice(bestIndex, 0, corner);
            } else {
                result.push(corner);
            }
        });

        result = result.filter(pt => unorderedSet.has(format(pt)));
        result = removeDuplicatePoints(result);

        return result;

        function pointToSegmentDistance(p, a, b) {
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            if (dx === 0 && dy === 0) return Math.hypot(p.x - a.x, p.y - a.y);

            const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy);
            const clampedT = Math.max(0, Math.min(1, t));
            const projX = a.x + clampedT * dx;
            const projY = a.y + clampedT * dy;
            return Math.hypot(p.x - projX, p.y - projY);
        }

        function removeDuplicatePoints(pts) {
            const seen = new Set();
            const out = [];
            for (const pt of pts) {
                const key = format(pt);
                if (!seen.has(key)) {
                    seen.add(key);
                    out.push(pt);
                }
            }
            return out;
        }
    }

    return (
        <Layer key={props.i} ref={layerRef}>
            {mask ? (
                <Line
                    ref={shapeRef}
                    points={points}
                    fill={color}
                    stroke={mask.polyStroke}
                    strokeWidth={strokeWidth}
                    opacity={mask.opacity}
                    closed={mask.polyClosed}
                    draggable={selected}
                    listening={!mask.locked}
                    onDragStart={(e) => handleLineDragStart(e)}
                    onDragEnd={(e) => handleLineDragEnd(e)}
                    onClick={handleSelect} onTap={handleSelect} />
            ) : null}

            {!isDragging &&
                overlapInfo
                    .filter(
                        (overlap) =>
                            overlap.mask1Id === props.mask.id || overlap.mask2Id === props.mask.id
                    )
                    .map((overlap, index) => {
                        const sortedPoints = sortPointsClockwise(overlap);
                        return sortedPoints.length > 2 ? (
                            <Line
                                key={index}
                                points={sortedPoints.flatMap(p => [p.x, p.y])}
                                fill="black"
                                closed
                                listening={false}
                                globalCompositeOperation="destination-out"
                            />
                        ) : null;
                    })}

            {Array.isArray(points) && points.length > 0 && !isDragging && selected &&
                points.map((_, index) => {
                    const x = points[index * 2];
                    const y = points[index * 2 + 1];
                    if (x && y && shapeRef.current) {
                        return (
                            <Circle
                                key={index}
                                x={x + shapeRef.current.x()}
                                y={y + shapeRef.current.y()}
                                radius={circleRadius}
                                fill="white"
                                stroke="black"
                                strokeWidth={Math.max(0.2, 1.5 / stageScale)}
                                draggable
                                onDragMove={(e) => handleCircleDragMove(e, index)}
                                onDragEnd={(e) => handleCircleDragEnd(e, index)}
                                onContextMenu={(e) => handleContextMenu(e, props.i, index)}
                            />
                        );
                    }
                    return null;
                })
            }


            {props.mask.name && !props.mask.visible && (
                <Text
                    text={props.mask.name}
                    x={textPosition.x}
                    y={textPosition.y}
                    align="center"
                    fontSize={getDynamicFontSize()}
                    fontStyle="bold"
                    fill="black"
                />
            )}
        </Layer>
    );
}
